/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AutocompleteLoadMoreEvent } from '@gh/core-ui';
import { isNotNil, setField, toString } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { first } from 'lodash';
import {
  JobOrderDetailsAttachment,
  JobOrderListCcItem,
  JobOrderListProduceOptions,
  JobOrderListSelectionFilter,
  JobOrderListSelectionType,
} from '../../../resources/job-order-list.model';

@Component({
  selector: 'gh-job-order-details-bottom-panel',
  template: require('./job-order-details-bottom-panel.component.html'),
  styles: [require('./job-order-details-bottom-panel.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobOrderDetailsBottomPanelComponent implements OnInit {
  @Input() attachments: JobOrderDetailsAttachment[];
  @Input() type: JobOrderListSelectionType;
  @Input() orderNumbers: number[];
  @Input() options: JobOrderListProduceOptions;
  @Input() editable: boolean;

  @Output() typeChange = new EventEmitter<JobOrderListSelectionType>();
  @Output() applyFilter = new EventEmitter<JobOrderListSelectionFilter>();
  @Output() optionsChange = new EventEmitter<JobOrderListProduceOptions>();
  @Output() openAttachments = new EventEmitter<void>();

  ccFrom?: JobOrderListCcItem;
  ccTo?: JobOrderListCcItem;
  orderNumber?: number;

  private costCenters: JobOrderListCcItem[];

  @Input()
  set costCenterCodes(codes: number[]) {
    this.costCenters = codes.map((code) => ({ code, name: toString(code) }));
  }

  get isCostCenterType(): boolean {
    return this.type === JobOrderListSelectionType.CostCenter;
  }

  get isOrderType(): boolean {
    return this.type === JobOrderListSelectionType.Order;
  }

  get isCcFromDefined(): boolean {
    return isNotNil(this.ccFrom);
  }

  get canBeApplied(): boolean {
    switch (this.type) {
      case JobOrderListSelectionType.CostCenter:
        return isNotNil(this.ccFrom) && isNotNil(this.ccTo);
      case JobOrderListSelectionType.Order:
        return isNotNil(this.orderNumber);
      default:
        return true;
    }
  }

  ngOnInit(): void {
    this.orderNumber = first(this.orderNumbers);
  }

  onTypeChange(type: JobOrderListSelectionType): void {
    this.typeChange.emit(type);
  }

  onOptionChanged(name: keyof JobOrderListProduceOptions, value: any): void {
    this.optionsChange.emit(setField(this.options, name, value));
  }

  onCcFromChange(from: JobOrderListCcItem): void {
    this.ccFrom = from;
    this.ccTo = void 0;
  }

  onCcToChange(to: JobOrderListCcItem): void {
    this.ccTo = to;
  }

  onApply(): void {
    if (this.isOrderType && isNotNil(this.orderNumber)) {
      this.applyFilter.emit({ type: JobOrderListSelectionType.Order, orderNumber: this.orderNumber });
    } else if (this.isCostCenterType && isNotNil(this.ccFrom) && isNotNil(this.ccTo)) {
      this.applyFilter.emit({ type: JobOrderListSelectionType.CostCenter, from: this.ccFrom.code, to: this.ccTo.code });
    }
  }

  onOpenAttachments(): void {
    this.openAttachments.emit();
  }

  loadMoreBeginCostCenters(event: AutocompleteLoadMoreEvent): void {
    const { query: { from, to, filter } } = event;
    event.load(of$(this.costCenters.filter(({ name }) => name.includes(filter || '')).slice(from, to)));
  }

  loadMoreEndCostCenters(event: AutocompleteLoadMoreEvent): void {
    const { query: { from, to, filter } } = event;
    event.load(of$(this.costCenters
      .filter((cc) => cc.code > this.ccFrom!.code) // tslint:disable-line:no-non-null-assertion
      .filter(({ name }) => name.includes(filter || '')).slice(from, to)));
  }
}
